import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Cta, Hero, Para, Question, Section, Steps, linkStyle } from './Landing';

// Answers "how many people can play Sequence" and the player-count questions
// people search for (2, 3, 4, 6 players). The table is the box rules; the
// site itself deals seven cards to everyone, which is said plainly below.

const COUNTS = [
    { players: '2', teams: '1 vs 1', cards: 7, win: '2' },
    { players: '3', teams: '1 vs 1 vs 1', cards: 6, win: '1' },
    { players: '4', teams: '2 vs 2', cards: 6, win: '2' },
    { players: '6', teams: '3 vs 3, or 2 vs 2 vs 2', cards: 5, win: '2 or 1' },
    { players: '8', teams: '4 vs 4', cards: 4, win: '2' },
    { players: '9', teams: '3 vs 3 vs 3', cards: 4, win: '1' },
    { players: '10', teams: '5 vs 5', cards: 3, win: '2' },
    { players: '12', teams: '6 vs 6, or 4 vs 4 vs 4', cards: 3, win: '2 or 1' },
];

const QUESTIONS = [
    {
        q: 'How many players can play Sequence?',
        a: 'Sequence is for 2 to 12 players. Everyone plays in 2 or 3 teams of the same size, so the counts that work are 2, 3, 4, 6, 8, 9, 10 and 12.',
    },
    {
        q: 'Can 3 people play Sequence?',
        a: 'Yes. Each of the three plays their own colour (green, blue or red), and the first to complete one sequence wins.',
    },
    {
        q: 'Can 2 people play Sequence?',
        a: 'Yes. Two players each take a colour, and the first to complete two sequences wins. Against the computer it is always a two-player game.',
    },
    {
        q: 'Can 5 or 7 people play Sequence?',
        a: 'Not in one game, because the teams have to be the same size. With 5 people, play 4 and rotate in; with 7, play 6 in two teams of three or three teams of two.',
    },
    {
        q: 'How many cards does each player get?',
        a: 'In the box rules: 7 cards with 2 players, 6 with 3 or 4, 5 with 6, 4 with 8 or 9, and 3 with 10 or 12. On Play Sequence everyone is dealt 7 cards, whatever the number of players.',
    },
];

const STRUCTURED_DATA = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: QUESTIONS.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
});

const cell = { color: '#3E4A45', fontSize: '0.95rem', px: { xs: 1, sm: 2 } };
const head = { ...cell, color: '#16201C', fontWeight: 600 };

const HowManyPlayers = () => (
    <Box>
        <Helmet>
            <title>How Many Players Can Play Sequence? 2, 3, 4 or 6 Players | Play Sequence</title>
            <meta name="description" content="Sequence is for 2 to 12 players in 2 or 3 equal teams. How to play Sequence with 2, 3, 4 or 6 players, how many cards each player gets, and how many sequences win." />
            <meta property="og:title" content="How Many Players Can Play Sequence?" />
            <meta property="og:description" content="2 to 12 players in 2 or 3 equal teams: cards per player, team set-ups and how many sequences win, for every player count." />
            <script type="application/ld+json">{STRUCTURED_DATA}</script>
        </Helmet>

        <Hero
            title="How Many Players Can Play Sequence?"
            subtitle="From 2 to 12, in 2 or 3 teams of the same size. Here's how each count plays."
        />

        <Container maxWidth="md" sx={{ py: 6 }}>
            <Section title="Every player count at a glance">
                <Para>
                    Sequence is played by individuals or teams. Everyone on a team shares one chip colour, and players sit so that the teams take turns one after another. With two teams you need two sequences to win; with three teams, one is enough.
                </Para>
                <TableContainer sx={{ mb: 2 }}>
                    <Table size="small" aria-label="Sequence player counts">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={head}>Players</TableCell>
                                <TableCell sx={head}>Teams</TableCell>
                                <TableCell sx={head}>Cards each</TableCell>
                                <TableCell sx={head}>To win</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {COUNTS.map(row => (
                                <TableRow key={row.players}>
                                    <TableCell sx={head}>{row.players}</TableCell>
                                    <TableCell sx={cell}>{row.teams}</TableCell>
                                    <TableCell sx={cell}>{row.cards}</TableCell>
                                    <TableCell sx={cell}>{row.win}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Para last>
                    Two teams (or two players) need two sequences to win; three need one. Cards each is the box rule. On Play Sequence everyone is dealt 7 cards whatever the player count, so you always have a full hand to plan with.
                </Para>
            </Section>

            <Section title="Sequence for 2 players">
                <Para>
                    The quickest game. Each player takes a colour and the first to make <strong>two sequences</strong> wins. Every chip you place is also a block, so watch your opponent's lines as closely as your own.
                </Para>
                <Para last>
                    No one to play with? <Link to="/play-against-computer" style={linkStyle}>Play Sequence against the computer</Link> on Easy, Medium or Hard. It's always a two-player game.
                </Para>
            </Section>

            <Section title="Sequence for 3 players">
                <Para>
                    Three players each play their own colour, green, blue and red, and the first to make <strong>one sequence</strong> wins. With two opponents you can't block everything, so the game turns on timing: block the player closest to five, and use a one-eyed Jack only when a line is one chip from done.
                </Para>
                <Para last>
                    To play online, start a game with <strong>Play with friends</strong>, choose 3 players and send the link to the other two.
                </Para>
            </Section>

            <Section title="Sequence for 4 players">
                <Para last>
                    Four players make two teams of two, sitting so the teams alternate. Partners can't show each other their cards or talk about them, so you read your partner from the chips they play. The first team to make <strong>two sequences</strong> wins. On Play Sequence you can see and talk to the whole table with the built-in video call.
                </Para>
            </Section>

            <Section title="Sequence for 6 or more players">
                <Steps items={[
                    <><strong>6 players:</strong> two teams of three (two sequences to win) or three teams of two (one sequence to win).</>,
                    <><strong>8 players:</strong> two teams of four.</>,
                    <><strong>9 players:</strong> three teams of three.</>,
                    <><strong>10 or 12 players:</strong> two teams of five or six, or, with 12, three teams of four.</>,
                ]} />
                <Para last>
                    Big games are slower between your turns, so plan two moves ahead and keep track of which spaces are already taken. For the full rules, see <Link to="/how-to-play" style={linkStyle}>how to play Sequence</Link>; for tactics, the <Link to="/strategy" style={linkStyle}>strategy guide</Link>.
                </Para>
            </Section>

            <Section title="Questions">
                {QUESTIONS.map(item => (
                    <Question key={item.q} q={item.q}>{item.a}</Question>
                ))}
            </Section>

            <Cta title="Got your players?" text="Start a game and share the link. Everyone joins from their own phone or computer." />
        </Container>
    </Box>
);

export default HowManyPlayers;
